import { useMemo } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMap, faChartLine, faEnvelope, faUser, faFileLines } from '@fortawesome/free-solid-svg-icons'
import { LAYOUT_DIMENSIONS } from '../hooks/useResponsiveLayout'
import { useReportsAccess } from '../hooks/useReports'

interface TabItem {
  path: string
  icon: typeof faMap
  label: string
  matchPaths: string[]
}

const tabs: TabItem[] = [
  {
    path: '/map',
    icon: faMap,
    label: 'Map',
    matchPaths: ['/', '/map'],
  },
  {
    path: '/charts',
    icon: faChartLine,
    label: 'Charts',
    matchPaths: ['/charts', '/compare'],
  },
  {
    path: '/reports',
    icon: faFileLines,
    label: 'Reports',
    matchPaths: ['/reports'],
  },
  {
    path: '/messages',
    icon: faEnvelope,
    label: 'Messages',
    matchPaths: ['/messages'],
  },
  {
    path: '/profile',
    icon: faUser,
    label: 'Account',
    matchPaths: ['/profile', '/preferences', '/settings'],
  },
]

export function BottomTabBar() {
  // Reports appears only for administrators and for users whose group has
  // a report configured; everyone else never sees the tab.
  const { data: hasReportsAccess } = useReportsAccess()
  const visibleItems = tabs.filter(
    (item) => item.path !== '/reports' || hasReportsAccess
  )
  const location = useLocation()
  const [searchParams] = useSearchParams()

  // Preserve sigfoxId query parameter across navigation
  const sigfoxId = searchParams.get('sigfoxId')
  const queryString = useMemo(() => {
    return sigfoxId ? `?sigfoxId=${sigfoxId}` : ''
  }, [sigfoxId])

  // Check if a tab is active based on current path
  const isActive = (tab: TabItem): boolean => {
    return tab.matchPaths.some((path) => {
      if (path === '/') {
        return location.pathname === '/'
      }
      return location.pathname.startsWith(path)
    })
  }

  return (
    <nav
      className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200"
      style={{
        height: LAYOUT_DIMENSIONS.BOTTOM_TAB_HEIGHT,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        zIndex: 9999, // Ensure always on top
      }}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Tab content */}
      <div className="flex items-stretch h-full">
        {visibleItems.map((tab) => {
          const active = isActive(tab)
          // Don't add query string to account pages
          const href =
            tab.path === '/profile' ? tab.path : `${tab.path}${queryString}`

          return (
            <Link
              key={tab.path}
              to={href}
              className={`flex-1 flex flex-col items-center justify-center min-h-[44px] transition-colors touch-manipulation ${
                active
                  ? 'text-blue-600'
                  : 'text-gray-400 hover:text-gray-600 active:text-gray-700'
              }`}
              aria-label={tab.label}
              aria-current={active ? 'page' : undefined}
            >
              <FontAwesomeIcon
                icon={tab.icon}
                className={`text-xl mb-1 ${active ? 'text-blue-600' : ''}`}
                fixedWidth
              />
              <span
                className={`text-xs font-medium ${
                  active ? 'text-blue-600' : ''
                }`}
              >
                {tab.label}
              </span>
            </Link>
          )
        })}
      </div>
    </nav>
  )
}
